import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import toast from "react-hot-toast";
import debounce from "lodash.debounce";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Pencil1Icon, TrashIcon, EnterIcon, ChevronLeftIcon } from "@radix-ui/react-icons";
import { Badge, Box, Button, Card, Flex, Spinner, Tabs, Text } from "@radix-ui/themes";

import { StudentSituationType } from "../../types/StudentSituation";
import { useGetStudentSituationsQuery } from "../../hooks/useGetStudentSituationsQuery";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { createStudentSituation, updateStudentSituation } from "../../services/school/studentSituation";
import { getStudentByCode } from "../../services/school/student/getStudentByCode";
import { routes } from "../../routes";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { setStudentSituationData } from "../../redux/slices/school/studentSituation";
import { StudentSituationFormValidation } from "./StudentSituationFormValidation";
import { getUserById } from "../../services/user/getUsers";
import { resetUserData, setUserData } from "../../redux/slices/users/user";
import { resetStudentData, setEnrollmentData, setStudentData } from "../../redux/slices/school/student";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import TextAreaFieldComponent from "../../components/inputs/TextAreafield/TextAreafieldComponent";
import { UserPreview } from "../Users/UserPreview";
import { isEmpty, set } from "lodash";
import { setPdfPreviewType } from "../../redux/slices/report/report";
import { CreateStudentType } from "../../types/Student";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

const columnNamesMap: { [key: string]: string } = {
  description: "Descrição",
  problem: "Problema",
  soluction: "Solução",
  isResolved: "Resolvido",
};

export const CreateStudentSituation = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: currentYear } = useGetCurrentAcademicYearQuery();
  const studentData = useSelector((state: RootState) => state.student);
  const userData = useSelector((state: RootState) => state.user);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("situations");
  const [selectedSituation, setSelectedSituation] = useState<StudentSituationType | null>(null);

  const mutationCreate = useMutation({ mutationFn: createStudentSituation, mutationKey: "createStudentSituation" });
  const mutationUpdate = useMutation({ mutationFn: updateStudentSituation, mutationKey: "updateStudentSituation" });

  const { control, handleSubmit, setValue, getValues, formState: { errors }, reset } = useForm<StudentSituationType>({
    resolver: yupResolver(StudentSituationFormValidation),
    defaultValues: {
      idStudent: "",
      description: "",
      problem: "",
      soluction: "",
      status: true,
      studentCode: '',
      isResolved: false,
      internalCode:''
    },
    mode: "onBlur",
  });
  
  const watchIdStudent = getValues("idStudent");
  const { data: situationsData,status:situationStatus,  isLoading: isLoadingSituations, refetch: fetchSituations } = useGetStudentSituationsQuery(
    { PageNumber: currentPage, PageSize: 100, IdStudent:watchIdStudent },
    { enabled: !!!watchIdStudent }
  );

  const { isLoading: isLoadingStudent, refetch: fetchStudentByCode } = useQuery(
    ["getStudentByCode"],
    async () => {
      try {
        const studentCodeValue = getValues("studentCode")?.trim();
        if (!studentCodeValue) return;
        const studentData = await getStudentByCode(studentCodeValue);
        if (!studentData) return;
        const userData = await getUserById(studentData.idUser);
        // const studentDataToStore:CreateStudentType = {
        //   user: userData as any,  
        //   documents: [],
        //   enrollment: {
        //    ...studentData as any
        //   },
        //   idFaculty: "",
        //   idDepartament: "",
        //   subscribeStudent: false,
        //   isInternStudent: false, 
        //   student: studentData
        // }
        dispatch(setUserData(userData))
        dispatch(setStudentData(studentData))
        setValue("idStudent", studentData?.id??"");
       await fetchSituations();
      } catch (error) {
        toast.error("Estudante não encontrado!");
        reset();
        dispatch(resetUserData())
        dispatch(resetStudentData())
      }
    },
    { enabled: false, refetchOnWindowFocus: false, retry: false }
  );

  const handleStudentCodeChange = useCallback(
    debounce((value: string) => {
      setValue("studentCode", value.trim());
      if(isEmpty(value)){
        dispatch(resetUserData())
        dispatch(resetStudentData())
      }
      if (value.length >= 3) {
        fetchStudentByCode();
        if(getValues('idStudent')){
          fetchSituations();
        }
      }
      
    }, 300),
    [setValue, fetchStudentByCode]
  );

  const handleDelete = async () => {
    if (selectedSituation) {
      selectedSituation.status = false;
      await mutationUpdate.mutateAsync(selectedSituation);
      fetchSituations();
      setSelectedSituation(null);
      toast.success("Situação excluída com sucesso!");
    }
  };

  const handleEdit = (situation: StudentSituationType) => {
    situation.studentCode = getValues('studentCode');
    dispatch(setStudentSituationData(situation));
    navigate(routes.updateStudentSituation);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      data.idAcademicYear = currentYear?.id;
      data.idEnrollment = studentData.student?.idEnrollment; 
      data.academicYear = currentYear?.year;
      await mutationCreate.mutateAsync(data);
      toast.success("Situação registrada!");
      fetchSituations();
      setActiveTab("situations");
      dispatch(setStudentSituationData(data));
      dispatch(setPdfPreviewType('STUDENT_SITUATION'));
      navigate(routes.pdfPreview);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao registrar situação");
    }
  });

  const options = situationsData?.items.map((situation) => [
    {
      label: "Editar",
      onClick: () => handleEdit(situation),
      icon: <Pencil1Icon />,
    },
    {
      label: "Excluir",
      onClick: () => setSelectedSituation(situation),
      icon: <TrashIcon />,
    },
  ]) || [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
const itemsSituations = situationsData?.items?.filter((item) => item.idStudent===getValues('idStudent')) || [];
  return (
    <Card size="5">
      <Flex direction="column" gap="6">
        <TextFieldComponent
          name="studentCode"
          label="Código do estudante"
          control={control}
          errors={errors}
          fieldProps={{
            placeholder: "Digite o código do estudante",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleStudentCodeChange(e.target.value),
          }}
        />
        <UserPreview />


        {isLoadingStudent ? (
          <Spinner loading />
        ) : (
          <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Trigger value="situations">
                Situações ({itemsSituations?.length || 0})
              </Tabs.Trigger>
              <Tabs.Trigger value="new">Nova Situação</Tabs.Trigger>
            </Tabs.List>
            <Box pt="3">
              <Tabs.Content value="situations">
                {isLoadingSituations ? (
                  <Spinner loading />
                ) : (
                  <Lists
                    status={situationStatus}
                    TableComponent={() => (
                      <TableComponent
                        columns={Object.keys(columnNamesMap)}
                        items={itemsSituations}  
                        options={options}
                        columnNamesMap={columnNamesMap}
                        totalPages={situationsData?.totalPages || 1}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                      />
                    )}
                  />
                )}
              </Tabs.Content>
              <Tabs.Content value="new">
                <form onSubmit={onSubmit} noValidate>
                  <Card size="1">
                    <Flex direction="column" gap="4">
                      <TextFieldComponent name="internalCode" label="Código interno" control={control} errors={errors} required={true}  />
                      <TextAreaFieldComponent name="description" label="Descrição" control={control} errors={errors} required={true} />
                      <TextAreaFieldComponent name="problem" label="Problema" control={control} errors={errors} />
                      <TextAreaFieldComponent name="reason" label="Motivo" control={control} errors={errors} />
                      <TextAreaFieldComponent name="soluction" label="Solução" control={control} errors={errors} />
                      <RadioGroupComponent
                      name="isResolved"
                      label="A situação foi resolvida?"
                      control={control}
                      errors={errors}
                      options={[
                        { value: "true", label: "Sim" },
                        { value: "false", label: "Não" },
                      ]}
                    />
                    
                      <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                        <Button color="red" onClick={() => setActiveTab("situations")} disabled={mutationCreate.isLoading}>
                          <ChevronLeftIcon /> Voltar
                        </Button>
                        <Button type="submit" disabled={mutationCreate.isLoading} variant="surface">
                          {mutationCreate.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                          Registrar
                        </Button>
                      </Flex>
                    </Flex>
                  </Card>
                </form>
              </Tabs.Content>
            </Box>
          </Tabs.Root>
        )}
      </Flex>
      <ConfirmAlertDialog
        open={!!selectedSituation}
        onOpenChange={(open) => !open && setSelectedSituation(null)}
        title="Esta operação é irreversível"
        item={selectedSituation}
        onConfirm={handleDelete}
        onCancel={() => setSelectedSituation(null)}
      />
    </Card>
  );
};
